// "Surveys" — Settings > CARE TEAM > Surveys. UAT F-486 (URS_12.3 / ACDPA-225).
//
// The 30/60/90-day surveys get their own category under Care Team, with "New" in green on any
// survey the patient has not answered yet — the tester's ask. Answered surveys stay listed
// below (URS_12.5 AC 3: history remains visible).
//
// ⛔ `listMySurveys()` IS ALSO WHAT ISSUES ONE (E12d/R2). The kernel has no scheduler by ruling;
// a milestone that fell due while the app was closed comes into existence on this read.
//
// ⭐ F-536 (URS_12.3): a survey issued with its INSTRUMENT (`survey.questions.questions`, the
// owner-approved 30/60/90-day questionnaire seeded in the kernel) is answered in full here —
// "Start survey" opens `SurveyQuestionnaire`, which renders the document verbatim. The kernel
// validates the answers and decides what needs staff attention.
// ⚠️ A survey issued before the instruments existed (`questions` is `{}`) keeps the old three
// buttons, so a patient holding one can still answer it.

import React, { useCallback, useEffect, useState } from "react";
import {
  IonButton,
  IonContent,
  IonIcon,
  IonNote,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSkeletonText,
  IonToast,
  RefresherEventDetail,
  useIonRouter,
} from "@ionic/react";
import { documentTextOutline } from "ionicons/icons";
import Header from "../../components/Header";
import NewMessageIndicator from "../../components/engagement/NewMessageIndicator";
import { useEngagementFeatures } from "../../hooks/useEngagementFeatures";
import { useHaptics } from "../../hooks/useHaptics";
import {
  Survey,
  engagementErrorMessage,
  listMySurveys,
  submitSurveyResponse,
} from "../../services/engagement";
import SurveyQuestionnaire from "./SurveyQuestionnaire";
import { instrumentOf } from "./surveyInstrument";
import "./Engagement.css";

/** A survey the patient has not answered yet — the kernel's own "outstanding" (AC 4). */
export const isNewSurvey = (survey: Survey): boolean => survey.response === null;

/**
 * F-538 (Leah, UAT — ruling 1): the completed row reads "30 day survey completed <date>" — one
 * line, a space before the date, regular weight, no bold (and the same for 60 and 90). The number
 * is the kernel's `cadence.offset_days` (lookup data), never a literal here.
 */
export const completedLine = (survey: Survey): string =>
  `${survey.cadence.offset_days} day survey completed ${shortDate(
    survey.completed_at ?? survey.response?.submitted_at ?? null,
  )}`.trim();

/** `2026-09-29` or an ISO instant → a short local date; never a raw ISO string on screen. */
const shortDate = (value: string | null): string => {
  if (!value) return "";
  const when = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
};

/**
 * What sits under a waiting survey: the questionnaire (F-536) — or, for a survey issued before
 * the instruments existed, the original three buttons.
 *
 * ⚠️ Answering needs surveys ON — hidden, not disabled, when they are off (the app mirrors the
 * portal's configuration, URS_12.5 AC 2).
 */
const SurveyAnswerArea: React.FC<{
  survey: Survey;
  enabled: boolean;
  open: boolean;
  busy: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSubmit: (answers: Record<string, unknown>) => void;
  onQuickAnswer: (pct: number) => void;
}> = ({ survey, enabled, open, busy, onOpen, onClose, onSubmit, onQuickAnswer }) => {
  const instrument = instrumentOf(survey);
  if (instrument) {
    if (!enabled) return null;
    return open ? (
      <SurveyQuestionnaire
        instrument={instrument}
        busy={busy}
        onSubmit={onSubmit}
        onCancel={onClose}
      />
    ) : (
      <div className="engagement-survey-actions">
        <IonButton size="small" disabled={busy} onClick={onOpen}>
          Start survey
        </IonButton>
      </div>
    );
  }
  return (
    <>
      <p>How is your therapy going since you started?</p>
      {enabled && (
        <div className="engagement-survey-actions">
          <IonButton size="small" fill="outline" disabled={busy} onClick={() => onQuickAnswer(30)}>
            Worse
          </IonButton>
          <IonButton size="small" fill="outline" disabled={busy} onClick={() => onQuickAnswer(60)}>
            The same
          </IonButton>
          <IonButton size="small" disabled={busy} onClick={() => onQuickAnswer(90)}>
            Better
          </IonButton>
        </div>
      )}
    </>
  );
};

const CareTeamSurveys: React.FC = () => {
  const router = useIonRouter();
  const { successActivity, errorFeedback } = useHaptics();
  const { features, isLoading: featuresLoading, hasOrganization } = useEngagementFeatures();

  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [answering, setAnswering] = useState<number | null>(null);
  /** The survey whose questionnaire is open (F-536) — one at a time. */
  const [openSurvey, setOpenSurvey] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await listMySurveys();
      setSurveys(data.items);
    } catch (err) {
      setError(engagementErrorMessage(err, "Your surveys could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await load();
    event.detail.complete();
  };

  const submit = async (
    survey: Survey,
    answers: Record<string, unknown>,
    scorePct: number | null,
  ) => {
    if (answering !== null) return;
    setAnswering(survey.id);
    try {
      await submitSurveyResponse(survey.id, answers, scorePct, null);
      await successActivity();
      setMessage("Survey submitted. Thank you.");
      setOpenSurvey(null);
      await load();
    } catch (err) {
      // ⛔ SURFACED, NEVER SWALLOWED — `SURVEYS_DISABLED` means the care team switched surveys
      // off after this screen loaded.
      await errorFeedback();
      setError(engagementErrorMessage(err, "Your survey could not be submitted."));
    } finally {
      setAnswering(null);
    }
  };

  /** A pre-F-536 survey with no questionnaire: the original three-button answer. */
  const answer = (survey: Survey, howAreThingsPct: number) =>
    submit(survey, { overall: howAreThingsPct }, howAreThingsPct);

  const waiting = surveys.filter(isNewSurvey);
  const answered = surveys.filter((survey) => !isNewSurvey(survey));
  const busy = isLoading || featuresLoading;

  return (
    <IonPage>
      {/* F-530 (Leah, UAT) — same header treatment as the Messages list (F-481) and the
          conversation screen (F-488): `Header` defaults `showGreeting`/`showUserIcon` to true,
          which put "Welcome," and the stacked-avatar row above "Surveys". Both off, and the
          same `engagement-messages-header` wrapper closes up the space they reserved. */}
      <div className="engagement-messages-header">
        <Header
          showGreeting={false}
          showUserIcon={false}
          showPageTitle
          pageTitle="Surveys"
          showBackButton
          onBackButtonClick={() => router.goBack()}
        />
      </div>
      <IonContent className="engagement-content">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        {busy ? (
          <div className="engagement-card">
            <IonSkeletonText animated style={{ width: "60%" }} />
            <IonSkeletonText animated style={{ width: "35%" }} />
          </div>
        ) : !hasOrganization ? (
          <div className="engagement-empty">
            <IonIcon icon={documentTextOutline} />
            <h2>No care team yet</h2>
            <p>Surveys become available once a provider is looking after your therapy.</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="engagement-empty">
            <IonIcon icon={documentTextOutline} />
            <h2>No surveys yet</h2>
            <p>
              {features.surveys_enabled
                ? `${features.org_name ?? "Your care team"} will send you a short survey 30, 60 and 90 days after you join.`
                : `${features.org_name ?? "Your care team"} is not sending surveys right now.`}
            </p>
          </div>
        ) : (
          <>
            {waiting.length > 0 && (
              <div className="engagement-card">
                <h2>
                  <IonIcon icon={documentTextOutline} /> Waiting for you
                </h2>
                {waiting.map((survey) => (
                  <div key={survey.id} className="engagement-survey" data-testid="survey-waiting">
                    <div>
                      <strong>{survey.cadence.label}</strong>{" "}
                      {/* F-495: the same green "New" as an unread care-team reply. */}
                      <NewMessageIndicator
                        count={1}
                        what={{ one: "survey waiting", many: "surveys waiting" }}
                      />
                      <IonNote>
                        {shortDate(survey.sent_at ?? survey.scheduled_for)}
                      </IonNote>
                    </div>
                    <SurveyAnswerArea
                      survey={survey}
                      enabled={features.surveys_enabled}
                      open={openSurvey === survey.id}
                      busy={answering !== null}
                      onOpen={() => setOpenSurvey(survey.id)}
                      onClose={() => setOpenSurvey(null)}
                      onSubmit={(answers) => void submit(survey, answers, null)}
                      onQuickAnswer={(pct) => void answer(survey, pct)}
                    />
                  </div>
                ))}
              </div>
            )}

            {answered.length > 0 && (
              <div className="engagement-card">
                <h2>Completed</h2>
                {answered.map((survey) => (
                  <div key={survey.id} className="engagement-survey" data-testid="survey-completed">
                    <p className="engagement-survey-completed">{completedLine(survey)}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <IonToast
          isOpen={!!message}
          message={message ?? ""}
          duration={3000}
          onDidDismiss={() => setMessage(null)}
        />
        <IonToast
          isOpen={!!error}
          message={error ?? ""}
          duration={5000}
          color="danger"
          onDidDismiss={() => setError(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default CareTeamSurveys;
